import React, { createContext, useCallback, useContext, useEffect, useLayoutEffect, useMemo, useRef, useState } from 'react'
import { gestureGateway } from '@/gestures/gateway'
import { hoverSuppression } from '@/state/hoverSuppression'

export type Anchor =
  | { type: 'element'; element: HTMLElement }
  | { type: 'point'; x: number; y: number }

export type Placement =
  | 'auto'
  | 'bottom-start'
  | 'bottom-end'
  | 'bottom-center'
  | 'top-start'
  | 'top-end'
  | 'top-center'
  | 'left'
  | 'left-center'
  | 'right'
  | 'right-center'
  | 'center'

export type OverlayCrossAxisBounds = {
  min: number
  max: number
}

export type OverlaySpec = {
  id?: string
  anchor: Anchor
  placement?: Placement
  offset?: number
  crossAxisBounds?: OverlayCrossAxisBounds
  closeOnOutside?: boolean
  tint?: boolean
  onClose?: () => void
  content: React.ReactNode | ((api: any) => React.ReactNode)
}

export type OverlayHandle = { id: string }

type OverlayItem = Required<Omit<OverlaySpec, 'id' | 'crossAxisBounds'>> & Pick<OverlaySpec, 'crossAxisBounds'> & { id: string }

export type OverlayAPI = {
  push: (overlay: OverlaySpec) => OverlayHandle
  pop: (id: string) => void
  popTop: () => void
  clear: () => void
}

const OVERLAY_VIEWPORT_MARGIN = 8

const OverlayContext = createContext<OverlayAPI | null>(null)
type OverlayStateContextValue = { stack: OverlayItem[]; hasOverlays: boolean }
const OverlayStateContext = createContext<OverlayStateContextValue | null>(null)

export function useOverlay() {
  const ctx = useContext(OverlayContext)
  if (!ctx) throw new Error('OverlayPortal not mounted')
  return ctx
}

export function useOverlayState() {
  const ctx = useContext(OverlayStateContext)
  if (!ctx) throw new Error('OverlayPortal state unavailable')
  return ctx
}

// Convenience helpers to reduce callsite boilerplate
export function useOverlayMenu() {
  const overlay = useOverlay()
  return {
    openAtElement: (el: HTMLElement, content: OverlaySpec['content'], opts?: Partial<Omit<OverlaySpec, 'anchor' | 'content'>>) =>
      overlay.push({ anchor: { type: 'element', element: el }, content, placement: 'bottom-start', offset: 8, closeOnOutside: true, ...(opts || {}) }),
    openAtPoint: (x: number, y: number, content: OverlaySpec['content'], opts?: Partial<Omit<OverlaySpec, 'anchor' | 'content'>>) =>
      overlay.push({ anchor: { type: 'point', x, y }, content, placement: 'auto', offset: 8, closeOnOutside: true, ...(opts || {}) }),
    openPanel: (content: OverlaySpec['content'], opts?: Partial<Omit<OverlaySpec, 'anchor' | 'content'>>) =>
      overlay.push({ anchor: { type: 'point', x: 0, y: 0 }, content, placement: 'bottom-start', offset: 0, closeOnOutside: true, ...(opts || {}) }),
    ...overlay,
  }
}

export function getOverlayAnchorRect(anchor: Anchor): { x: number; y: number; width: number; height: number } {
  if (anchor.type === 'point') return { x: anchor.x, y: anchor.y, width: 0, height: 0 }
  const r = anchor.element.getBoundingClientRect()
  return { x: r.left, y: r.top, width: r.width, height: r.height }
}

export function computeOverlayPosition(
  anchorRect: { x: number; y: number; width: number; height: number },
  overlaySize: { width: number; height: number },
  placement: Placement,
  offset: number,
  crossAxisBounds?: OverlayCrossAxisBounds,
) {
  const vw = window.innerWidth
  const vh = window.innerHeight
  const margin = OVERLAY_VIEWPORT_MARGIN
  const positionSize = {
    width: Math.min(overlaySize.width, Math.max(1, vw - margin * 2)),
    height: Math.min(overlaySize.height, Math.max(1, vh - margin * 2)),
  }

  const positions: Placement[] = placement === 'auto'
    ? ['bottom-start', 'bottom-end', 'top-start', 'top-end', 'right', 'left']
    : [placement]

  for (const p of positions) {
    let left = 0, top = 0
    switch (p) {
      case 'bottom-start':
        left = anchorRect.x
        top = anchorRect.y + anchorRect.height + offset
        break
      case 'bottom-end':
        left = anchorRect.x + anchorRect.width - positionSize.width
        top = anchorRect.y + anchorRect.height + offset
        break
      case 'bottom-center':
        left = anchorRect.x + anchorRect.width / 2 - positionSize.width / 2
        top = anchorRect.y + anchorRect.height + offset
        break
      case 'top-start':
        left = anchorRect.x
        top = anchorRect.y - positionSize.height - offset
        break
      case 'top-end':
        left = anchorRect.x + anchorRect.width - positionSize.width
        top = anchorRect.y - positionSize.height - offset
        break
      case 'top-center':
        left = anchorRect.x + anchorRect.width / 2 - positionSize.width / 2
        top = anchorRect.y - positionSize.height - offset
        break
      case 'right':
        left = anchorRect.x + anchorRect.width + offset
        top = anchorRect.y
        break
      case 'right-center':
        left = anchorRect.x + anchorRect.width + offset
        top = anchorRect.y + anchorRect.height / 2 - positionSize.height / 2
        break
      case 'left':
        left = anchorRect.x - positionSize.width - offset
        top = anchorRect.y
        break
      case 'left-center':
        left = anchorRect.x - positionSize.width - offset
        top = anchorRect.y + anchorRect.height / 2 - positionSize.height / 2
        break
      case 'center':
        left = (vw - positionSize.width) / 2
        top = (vh - positionSize.height) / 2
        break
    }
    if (crossAxisBounds) {
      if (p === 'left' || p === 'left-center' || p === 'right' || p === 'right-center') {
        top = clampOverlayAxis(top, crossAxisBounds.min, crossAxisBounds.max - positionSize.height)
      } else if (p === 'top-start' || p === 'top-end' || p === 'top-center' || p === 'bottom-start' || p === 'bottom-end' || p === 'bottom-center') {
        left = clampOverlayAxis(left, crossAxisBounds.min, crossAxisBounds.max - positionSize.width)
      }
    }
    // Clamp within viewport with a small margin
    const cl = Math.max(margin, Math.min(left, vw - positionSize.width - margin))
    const ct = Math.max(margin, Math.min(top, vh - positionSize.height - margin))
    // If we didn't have to push back too much, accept; otherwise try next
    const ok = Math.abs(cl - left) < 48 && Math.abs(ct - top) < 48
    if (ok || placement !== 'auto') return { left: cl, top: ct }
  }
  // Fallback to bottom-start clamped
  const left = Math.max(margin, Math.min(anchorRect.x, window.innerWidth - positionSize.width - margin))
  const top = Math.max(margin, Math.min(anchorRect.y + anchorRect.height + offset, window.innerHeight - positionSize.height - margin))
  return { left, top }
}

function clampOverlayAxis(value: number, min: number, max: number): number {
  if (max < min) return min
  return Math.max(min, Math.min(value, max))
}

// Note: outside click is handled by a single backdrop element in OverlayPortal

function eventTargetInsideOverlay(target: EventTarget | null, overlayId: string): boolean {
  let node = target instanceof Element ? target : null
  while (node) {
    if (node instanceof HTMLElement && node.dataset.overlayId === overlayId) return true
    node = node.parentElement
  }
  return false
}

function OverlayItemView({ item, zIndex, onRequestClose }: { item: OverlayItem; zIndex: number; onRequestClose: (id: string) => void }) {
  const ref = useRef<HTMLDivElement | null>(null)
  const [style, setStyle] = useState<{ left: number; top: number; visibility: 'hidden' | 'visible' }>({ left: 0, top: 0, visibility: 'hidden' })

  const updatePosition = useCallback(() => {
    const el = ref.current
    if (!el) return
    // If anchor element has been removed, close this overlay
    if (item.anchor.type === 'element' && !item.anchor.element.isConnected) {
      onRequestClose(item.id)
      return
    }
    const r = el.getBoundingClientRect()
    const anchorRect = getOverlayAnchorRect(item.anchor)
    const pos = computeOverlayPosition(anchorRect, { width: r.width, height: r.height }, item.placement, item.offset, item.crossAxisBounds)
    setStyle({ left: pos.left, top: pos.top, visibility: 'visible' })
  }, [item.anchor, item.crossAxisBounds, item.placement, item.offset])

  useLayoutEffect(() => {
    updatePosition()
    const onViewport = () => updatePosition()
    window.addEventListener('resize', onViewport)
    window.addEventListener('scroll', onViewport, true)

    // Reposition when overlay content resizes
    let ro: ResizeObserver | null = null
    let mo: MutationObserver | null = null
    ro = new ResizeObserver(() => updatePosition())
    if (ref.current) ro.observe(ref.current)
    if (item.anchor.type === 'element') {
      const elAnchor = item.anchor.element
      if (elAnchor) ro.observe(elAnchor)
    }

    // Observe DOM mutations to remove overlay if anchor is detached
    if (item.anchor.type === 'element' && typeof MutationObserver !== 'undefined') {
      try {
        // If already detached, close immediately
        const elAnchor = item.anchor.element
        if (!elAnchor || !elAnchor.isConnected) {
          onRequestClose(item.id)
        } else {
          mo = new MutationObserver(() => {
            const el = item.anchor.type === 'element' ? item.anchor.element : null
            if (!el || !el.isConnected) {
              try { onRequestClose(item.id) } catch { }
            }
          })
          mo.observe(document.documentElement || document.body, { childList: true, subtree: true })
        }
      } catch { }
    }

    return () => {
      window.removeEventListener('resize', onViewport)
      window.removeEventListener('scroll', onViewport, true)
      try { ro && ro.disconnect() } catch { }
      try { mo && mo.disconnect() } catch { }
    }
  }, [updatePosition, item.anchor])

  // Prevent overlay pointer events from reaching the app below, but only after
  // target handlers have run. Use bubble phase so child elements still receive
  // their own onPointerDown handlers (e.g., canvases in color pickers).
  const onPointerDownBubble = useCallback((e: React.PointerEvent) => {
    try { e.stopPropagation() } catch { }
  }, [])

  const content = typeof item.content === 'function' ? (item.content as any)({ close: () => onRequestClose(item.id) }) : item.content

  return (
    <div
      ref={ref}
      style={{
        position: 'fixed',
        left: style.left,
        top: style.top,
        visibility: style.visibility,
        zIndex,
        maxWidth: `calc(100vw - ${OVERLAY_VIEWPORT_MARGIN * 2}px)`,
        maxHeight: `calc(100dvh - ${OVERLAY_VIEWPORT_MARGIN * 2}px)`,
        overflow: 'auto',
        overscrollBehavior: 'contain',
      }}
      className="pointer-events-auto"
      onPointerDown={onPointerDownBubble}
      data-overlay-id={item.id}
    >
      {content}
    </div>
  )
}

export function OverlayPortal({ children }: { children?: React.ReactNode }) {
  const [stack, setStack] = useState<OverlayItem[]>([])
  const rootRef = useRef<HTMLDivElement | null>(null)
  const prevLenRef = useRef(0)

  const api = useMemo<OverlayAPI>(() => ({
    push: (spec: OverlaySpec) => {
      const id = spec.id || `ovl-${Date.now()}-${Math.random().toString(36).slice(2, 8)}`
      const item: OverlayItem = {
        id,
        anchor: spec.anchor,
        placement: spec.placement ?? 'auto',
        offset: spec.offset ?? 8,
        crossAxisBounds: spec.crossAxisBounds,
        closeOnOutside: spec.closeOnOutside ?? true,
        tint: spec.tint ?? false,
        onClose: spec.onClose || (() => { }),
        content: spec.content,
      }
      setStack((s) => [...s, item])
      return { id }
    },
    pop: (id: string) => {
      let cb: (() => void) | null = null
      setStack((s) => {
        const idx = s.findIndex((x) => x.id === id)
        if (idx >= 0) cb = s[idx].onClose || null
        return s.filter((x) => x.id !== id)
      })
      if (cb) {
        try { queueMicrotask(cb) } catch { setTimeout(cb, 0) }
      }
    },
    popTop: () => {
      let cb: (() => void) | null = null
      setStack((s) => {
        if (s.length === 0) return s
        cb = s[s.length - 1].onClose || null
        return s.slice(0, -1)
      })
      if (cb) {
        try { queueMicrotask(cb) } catch { setTimeout(cb, 0) }
      }
    },
    clear: () => {
      let cbs: Array<(() => void) | undefined> = []
      setStack((s) => { cbs = s.map((it) => it.onClose); return [] })
      if (cbs.length) {
        const run = () => { for (const fn of cbs) { try { fn && fn() } catch { } } }
        try { queueMicrotask(run) } catch { setTimeout(run, 0) }
      }
    },
  }), [])

  // Centralized hover suppression: when overlay stack is non-empty, suppress hover;
  // when it returns to empty, restore previous suppression state.
  useEffect(() => {
    const prev = prevLenRef.current
    const curr = stack.length
    if (prev === 0 && curr > 0) {
      hoverSuppression.set('overlay', true)
    } else if (prev > 0 && curr === 0) {
      hoverSuppression.set('overlay', false)
    }
    prevLenRef.current = curr
  }, [stack])

  useEffect(() => () => hoverSuppression.reset('overlay'), [])

  // Dismiss top on Escape via central gateway
  useEffect(() => {
    const unsub = gestureGateway.subscribe(e => {
      if (e.type === 'KEY_DOWN' && e.key === 'Escape') setStack((s) => s.slice(0, -1))
    })
    return () => { unsub?.() }
  }, [])

  useEffect(() => {
    const top = stack[stack.length - 1]
    if (!top?.closeOnOutside) return

    const handlePointerDown = (event: PointerEvent) => {
      if (eventTargetInsideOverlay(event.target, top.id)) return
      event.preventDefault()
      event.stopPropagation()
      api.pop(top.id)
    }

    document.addEventListener('pointerdown', handlePointerDown, true)
    return () => document.removeEventListener('pointerdown', handlePointerDown, true)
  }, [api, stack])

  const onRequestClose = useCallback((id: string) => api.pop(id), [api])

  const hasOverlays = stack.length > 0
  // Ensure overlays render above app UI (toolbar z-[100001]).
  const baseZ = 200000
  const topIndex = stack.length - 1
  const topZ = baseZ + topIndex * 2 + 1 // content z
  const backdropZ = topZ - 1
  const top = topIndex >= 0 ? stack[topIndex] : null
  const tinted = !!top?.tint

  return (
    <OverlayContext.Provider value={api}>
      <OverlayStateContext.Provider value={{ stack, hasOverlays }}>
        {children}
        <div ref={rootRef} className="fixed inset-0 z-[200000] pointer-events-none">
          {hasOverlays && (
            <div
              className="fixed inset-0 pointer-events-auto"
              style={{ zIndex: backdropZ, background: tinted ? 'rgba(0,0,0,0.4)' : 'transparent' }}
              onPointerDown={(e) => {
                e.preventDefault(); e.stopPropagation()
              }}
            />
          )}
          {stack.map((item, i) => (
            <OverlayItemView key={item.id} item={item} zIndex={baseZ + i * 2 + 1} onRequestClose={onRequestClose} />
          ))}
        </div>
      </OverlayStateContext.Provider>
    </OverlayContext.Provider>
  )
}
